class QuantityPopover extends HTMLElement {
  constructor() {
    super();
    this.debounceTimer = null;
    this.isUpdating = false;
    this.maxRetries = 3;
    this.retryCount = 0;
    this.maxQuantity = parseInt(this.getAttribute('data-max-quantity')) || 9999;

    this.handlePlus = this.handlePlus.bind(this);
    this.handleMinus = this.handleMinus.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleManualInput = this.handleManualInput.bind(this);
  }

  connectedCallback() {
    this.quantity = parseInt(this.getAttribute('data-quantity')) || 1;
    this.lineId = this.getAttribute('data-line-id');
    this.variantId = this.getAttribute('data-variant-id');

    this.minusButton = this.querySelector('button[name="minus"]');
    this.plusButton = this.querySelector('button[name="plus"]');
    this.inputField = this.querySelector('input.drawer-qty-input');

    const removeButtonComponent = this.querySelector('cart-remove-button');
    this.deleteButton = removeButtonComponent?.querySelector('button');

    this.updateMinusButtonState();

    if (this.plusButton) {
      this.plusButton.addEventListener('click', this.handlePlus);
    }
    if (this.minusButton) {
      this.minusButton.addEventListener('click', this.handleMinus);
    }
    if (this.deleteButton) {
      this.deleteButton.addEventListener('click', this.handleDelete);
    }
    if (this.inputField) {
      this.inputField.addEventListener('change', this.handleManualInput);
    }
  }

  disconnectedCallback() {
    if (this.plusButton) {
      this.plusButton.removeEventListener('click', this.handlePlus);
    }
    if (this.minusButton) {
      this.minusButton.removeEventListener('click', this.handleMinus);
    }
    if (this.deleteButton) {
      this.deleteButton.removeEventListener('click', this.handleDelete);
    }
    if (this.inputField) {
      this.inputField.removeEventListener('change', this.handleManualInput);
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  updateMinusButtonState() {
    if (this.quantity <= 1 && this.minusButton) {
      this.minusButton.disabled = true;
    } else if (this.minusButton) {
      this.minusButton.disabled = false;
    }
  }

  handlePlus() {
    updateCartCountBubble(1);
    this.quantity += 1;
    this.inputField.value = this.quantity;
    this.updateMinusButtonState();
    this.debouncedUpdateCart();
  }

  handleMinus() {
    if (this.quantity > 1) {
        updateCartCountBubble(-1);
      this.quantity -= 1;
      this.inputField.value = this.quantity;
      this.updateMinusButtonState();
      this.debouncedUpdateCart();
    }
  }

  handleManualInput() {
    let newQuantity = parseInt(this.inputField.value);

    if (isNaN(newQuantity) || newQuantity < 0) {
      this.inputField.value = this.quantity;
      return;
    }

    if (newQuantity > this.maxQuantity) {
      newQuantity = this.maxQuantity;
      this.inputField.value = newQuantity;
    }

    this.quantity = newQuantity;

    if (this.quantity === 0) {
      this.handleDelete();
      return;
    }

    this.updateMinusButtonState();
    this.debouncedUpdateCart();
  }

  handleDelete(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    console.log('Delete button clicked for item with key:', this.lineId);

    this.quantity = 0;
    if (this.inputField) {
      this.inputField.value = 0;
    }

    const prodRowId = `CartDrawer-Item-${this.lineId}`;
    const prodRow = document.getElementById(prodRowId);
    if (prodRow) {
      prodRow.classList.add('text-loading');
    }

    this.updateCart();
  }

  debouncedUpdateCart() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.updateCart();
    }, 500);
  }

  async updateCart() {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.setLoadingState(true);

    const payload = {
      updates: {
        [this.lineId]: this.quantity,
      },
      sections: 'cart-drawer',
    };

    try {
      const response = await Promise.race([
        fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 5000)),
      ]);

      if (!response.ok) {
        throw new Error(`Network response error: ${response.status}`);
      }

      const data = await response.json();
      this.updateCartUI(data);

      this.retryCount = 0;

      this.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cartData: data },
        })
      );
    } catch (error) {
      console.error('Error updating cart:', error);

      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        console.log(`Retrying cart update (${this.retryCount}/${this.maxRetries})...`);
        setTimeout(() => {
          this.isUpdating = false;
          this.updateCart();
        }, 1000);
        return;
      }

      this.showErrorMessage('Failed to update cart. Please try again.');
    } finally {
      if (this.retryCount === 0 || this.retryCount >= this.maxRetries) {
        this.setLoadingState(false);
        this.isUpdating = false;
      }
    }
  }

  setLoadingState(isLoading) {
    const elements = {
      checkoutButton: document.getElementById('CartDrawer-Checkout'),
      subtotalPrice: document.getElementById('live-subtotal-price'),
      prodRow: document.getElementById(`CartDrawer-Item-${this.lineId}`),
    };

    const lineItemPrice = elements.prodRow?.querySelector('.drawer-item-price');

    if (isLoading) {
      elements.subtotalPrice?.classList.add('text-loading');
      if (elements.checkoutButton) {
        elements.checkoutButton.disabled = true;
        elements.checkoutButton.classList.add('text-loading');
      }
      elements.prodRow?.classList.add('text-loading');
      lineItemPrice?.classList.add('text-loading');
    } else {
      elements.subtotalPrice?.classList.remove('text-loading');
      if (elements.checkoutButton) {
        elements.checkoutButton.disabled = false;
        elements.checkoutButton.classList.remove('text-loading');
      }
      elements.prodRow?.classList.remove('text-loading');
      lineItemPrice?.classList.remove('text-loading');
    }
  }

  updateCartUI(data) {
    const newCartData = data['sections']['cart-drawer'];
    const cartCountBubble = document.querySelector('.cart-count-bubble > span:first-child');

    let numItems = 0;
    let hasMutatedCartCountBubble = false;
    if (cartCountBubble) {
    numItems = cartCountBubble.textContent;
    } else {
        // FS quick add mutates the cart count bubble, here is how to handle for now
        const mutatedCartCountBubble = document.querySelector('.cart-count-bubble');
        if (mutatedCartCountBubble) {
            hasMutatedCartCountBubble = true;
            numItems = mutatedCartCountBubble.textContent;
        }
    }

    let diff = parseInt(data['item_count']) - parseInt(numItems);
    updateCartCountBubble(diff, hasMutatedCartCountBubble);

    if (!newCartData) {
      console.error('Missing cart-drawer section in response');
      return;
    }

    try {
      const newCart = new DOMParser().parseFromString(newCartData, 'text/html');
      const newCartDrawer = newCart.querySelector('cart-drawer');
      const cartDrawer = document.querySelector('cart-drawer');

      if (newCartDrawer && cartDrawer) {
        cartDrawer.innerHTML = newCartDrawer.innerHTML;
      } else {
        console.error('Could not find cart-drawer element');
      }
    } catch (error) {
      console.error('Error updating cart UI:', error);
    }
  }

  showErrorMessage(message) {
    let errorEl = document.getElementById('cart-error-message');

    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.id = 'cart-error-message';
      errorEl.className = 'cart-error-message';

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.prepend(errorEl);
      }
    }

    errorEl.textContent = message;
    errorEl.style.display = 'block';

    setTimeout(() => {
      errorEl.style.display = 'none';
    }, 5000);
  }
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
    });
  }
}

class AddToCart extends HTMLElement {
  constructor() {
    super();
    this.maxRetries = 3;
    this.retryCount = 0;
    this.form = this.querySelector('form');

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    } else {
      console.error('AddToCart: No form found inside the component');
    }

    this.isCartOpen = false;
    document.addEventListener('cart:opened', () => (this.isCartOpen = true));
    document.addEventListener('cart:closed', () => (this.isCartOpen = false));
  }

  handleSubmit(event) {
    event.preventDefault();
    this.updateCart();
  }



  async updateCart() {
    const submitButton = this.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.classList.add('loading');
      submitButton.disabled = true;
    }

    try {
      const formData = new FormData(this.form);

      const response = await Promise.race([
        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 5000)),
      ]);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Error adding to cart');
      }

      const data = await response.json();
      console.log("data:", data)

      this.retryCount = 0;

      await this.refreshCartDrawer();
      updateCartCountBubble(1);

      const event = new CustomEvent('product:added-to-cart', {
        bubbles: true,
        detail: { product: data },
      });
      this.dispatchEvent(event);
    } catch (error) {
      console.error('Error adding product to cart:', error);

      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        console.log(`Retrying add to cart (${this.retryCount}/${this.maxRetries})...`);
        setTimeout(() => this.updateCart(), 1000);
        return;
      }

      this.showErrorMessage(error.message || 'Failed to add item to cart');
    } finally {
      if (this.retryCount === 0 || this.retryCount >= this.maxRetries) {
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.disabled = false;
        }
      }
    }
  }

  async refreshCartDrawer() {
    try {
      const refreshCart = await Promise.race([
        fetch(window.Shopify.routes.root + '?sections=cart-drawer', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 5000)),
      ]);

      const newCart = await refreshCart.json();
      console.log("newCart:", newCart)

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && newCart['cart-drawer']) {
        const html = new DOMParser().parseFromString(newCart['cart-drawer'], 'text/html');
        const parsedCartDrawer = html.querySelector('cart-drawer');

        if (parsedCartDrawer) {
          requestAnimationFrame(() => {
            cartDrawer.innerHTML = parsedCartDrawer.innerHTML;

            if (!this.isCartOpen) {
              cartDrawer.classList.add('animate', 'active');

              document.dispatchEvent(new CustomEvent('cart:opened'));
            }
          });
        }
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
      throw error;
    }
  }

  showErrorMessage(message) {
    const errorToast = document.createElement('div');
    errorToast.className = 'add-to-cart-error-toast';
    errorToast.textContent = message;

    Object.assign(errorToast.style, {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      background: '#f44336',
      color: 'white',
      padding: '12px 20px',
      borderRadius: '4px',
      zIndex: '9999',
      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
      opacity: '0',
      transition: 'opacity 0.3s ease',
    });

    document.body.appendChild(errorToast);

    setTimeout(() => {
      errorToast.style.opacity = '1';
    }, 10);

    setTimeout(() => {
      errorToast.style.opacity = '0';
      setTimeout(() => {
        document.body.removeChild(errorToast);
      }, 300);
    }, 5000);
  }
}

customElements.define('quantity-popover', QuantityPopover);
customElements.define('cart-remove-button', CartRemoveButton);
customElements.define('add-to-cart-button', AddToCart);


function updateCartCountBubble(delta, mutatedCartCountBubble = false) {
    const cartCountBubble = document.querySelector('.cart-count-bubble');
    if (!cartCountBubble) return;
    
    if (mutatedCartCountBubble) {
        let currCount = parseInt(cartCountBubble.innerText);
        currCount += delta;
        cartCountBubble.innerText = currCount;
        return;
    }
    const countSpan = cartCountBubble.querySelector('span:first-child');
    const visibleCountSpan = cartCountBubble.querySelector('.visually-hidden');
    
    if (countSpan) {
      let currentCount = parseInt(countSpan.textContent) || 0;
      currentCount += delta;
      // Ensure count doesn't go below zero
      currentCount = Math.max(0, currentCount);
      countSpan.textContent = currentCount;
      
      if (visibleCountSpan) {
        // Replace only the number part, keeping "items in cart" text
        visibleCountSpan.textContent = visibleCountSpan.textContent.replace(/\d+/, currentCount) || `${currentCount} items in cart`;
      }
    }
  }